import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const emptyItem = {
  itemName: '', unit: 'pcs', rack: null, floor: null, quantity: '', availableQty: null, rackStorageId: null,
  invoiceNo: '', invoiceDate: '', partDescription: '', partNumber: '', partRev: '',
  boxQty: '', invoiceQty: '', batchNo: '',
};

const DispatchForm = () => {
  const navigate = useNavigate();
  const [clients, setClients]   = useState([]);
  const [clientId, setClientId] = useState('');
  const [outwardDocNo, setOutwardDocNo]   = useState('');
  const [outwardDate, setOutwardDate]     = useState('');
  const [customerPO, setCustomerPO]       = useState('');
  const [ewayBillNumber, setEwayBillNumber] = useState('');
  const [vehicleDetail, setVehicleDetail] = useState('');
  const [consignee, setConsignee]         = useState('');
  const [items, setItems]   = useState([{ ...emptyItem }]);
  const [loading, setLoading] = useState(false);
  const [stockOptions, setStockOptions] = useState([]); // every in-stock row for the chosen customer
  const [loadingStock, setLoadingStock] = useState(false);
  const [truncated, setTruncated]       = useState(false);
  const [filters, setFilters]           = useState({}); // { [rowIndex]: filter text }

  useEffect(() => {
    api.get('/clients').then((r) => setClients(r.data.data)).catch(console.error);
  }, []);

  // Load the dispatchable stock for the chosen customer. This is what fills the
  // item dropdown on every row, so it is fetched once per customer, not per row.
  useEffect(() => {
    if (!clientId) { setStockOptions([]); setTruncated(false); return; }
    setLoadingStock(true);
    api.get('/rack/search', { params: { client: clientId } })
      .then((r) => { setStockOptions(r.data.data || []); setTruncated(!!r.data.truncated); })
      .catch((err) => {
        setStockOptions([]);
        toast.error(err.response?.data?.message || 'Could not load stock for this customer');
      })
      .finally(() => setLoadingStock(false));
  }, [clientId]);

  // Label shown for one stock row in the dropdown.
  const optionLabel = (o) => {
    const part = o.product?.partNumber || o.product?.projectName || o.product?.sku;
    const where = `${o.rack?.code ?? '—'} · ${o.floor ?? '—'}`;
    return `${part ? part + ' — ' : ''}${o.itemName}  (${o.quantity} ${o.unit || 'pcs'} · ${where})`;
  };

  // Rows matching this row's filter box. Matching covers item name, part number,
  // part description and project name, so the filter behaves like the search did.
  const optionsFor = (i) => {
    const q = (filters[i] || '').trim().toLowerCase();
    if (!q) return stockOptions;
    return stockOptions.filter((o) => {
      const p = o.product || {};
      return [o.itemName, p.partNumber, p.partDescription, p.projectName, p.sku, p.name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  };

  const applySelection = (i, found) => {
    const updated = [...items];
    if (!found) {
      updated[i] = {
        ...updated[i],
        itemName: '', partDescription: '', partNumber: '', partRev: '',
        rack: null, floor: null, availableQty: null, rackStorageId: null,
      };
      setItems(updated);
      return;
    }
    const p = found.product || {};
    updated[i] = {
      ...updated[i],
      // Item Name and Part Description are one and the same value here; both are
      // sent so existing dispatch records and downstream paperwork stay intact.
      itemName: found.itemName,
      partDescription: found.itemName,
      partNumber: p.partNumber || p.projectName || p.sku || updated[i].partNumber || '',
      partRev: p.partRevision || updated[i].partRev || '',
      rack: found.rack,
      floor: found.floor,
      unit: found.unit || p.unit || 'pcs',
      availableQty: found.quantity,
      rackStorageId: found._id,
    };
    setItems(updated);
  };

  const selectByStorageId = (i, storageId) =>
    applySelection(i, storageId ? stockOptions.find((o) => o._id === storageId) : null);

  const updateItem = (i, field, value) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: value };
    setItems(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) return toast.error('Please select the client (goods owner)');
    const unselected = items.find((i) => !i.itemName);
    if (unselected) return toast.error('Select an item / part for every row before saving');
    const invalid = items.find((i) => i.availableQty !== null && Number(i.quantity) > i.availableQty);
    if (invalid) return toast.error(`Qty for "${invalid.itemName}" exceeds available stock`);

    setLoading(true);
    try {
      // GRN Reference is no longer collected at creation. The field remains on the
      // model, so dispatches created earlier keep their stored reference.
      await api.post('/dispatch', {
        clientId,
        consignee,
        outwardDocNo,
        outwardDate: outwardDate || undefined,
        customerPO,
        ewayBillNumber,
        vehicleDetail,
        items: items.map((i) => ({
          itemName: i.itemName,
          quantity: Number(i.quantity),
          unit: i.unit || 'pcs',
          invoiceNo: i.invoiceNo,
          invoiceDate: i.invoiceDate || undefined,
          // Merged in the UI into one "Item Name / Part Description" field; both
          // model fields are still populated so existing consumers keep working.
          partDescription: i.partDescription || i.itemName,
          partNumber: i.partNumber,
          partRev: i.partRev,
          boxQty: i.boxQty === '' ? undefined : Number(i.boxQty),
          invoiceQty: i.invoiceQty === '' ? undefined : Number(i.invoiceQty),
          batchNo: i.batchNo,
        })),
      });
      toast.success('Dispatch created! Proceed with QC inspection.');
      navigate('/dispatch');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create dispatch');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-5">
      {/* Header fields */}
      <div className="zip-card p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Customer (Goods Owner) *</label>
            <select
              className="zip-input"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setItems([{ ...emptyItem }]);
                setFilters({});
              }}
            >
              <option value="">Select Customer</option>
              {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.code})</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Outward Doc No. (manual)</label>
            <input type="text" className="zip-input" value={outwardDocNo}
              onChange={(e) => setOutwardDocNo(e.target.value)} placeholder="OUT-2024-001" />
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Outward Date</label>
            <input type="date" className="zip-input" value={outwardDate}
              onChange={(e) => setOutwardDate(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Customer PO</label>
            <input type="text" className="zip-input" value={customerPO}
              onChange={(e) => setCustomerPO(e.target.value)} placeholder="CPO-001" />
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Consignee / Destination</label>
            <input type="text" className="zip-input" value={consignee}
              onChange={(e) => setConsignee(e.target.value)} placeholder="Where the goods are going" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Eway Bill Number</label>
            <input type="text" className="zip-input" value={ewayBillNumber}
              onChange={(e) => setEwayBillNumber(e.target.value)} placeholder="EWB-001" />
          </div>
          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Vehicle Detail</label>
            <input type="text" className="zip-input" value={vehicleDetail}
              onChange={(e) => setVehicleDetail(e.target.value)} placeholder="MH-12-AB-1234" />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="zip-card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zip-text tracking-tight">Items to Dispatch</h3>
          <button
            type="button"
            onClick={() => setItems([...items, { ...emptyItem }])}
            className="btn-ghost flex items-center gap-1.5 text-xs"
          >
            <Plus size={13} /> Add Item
          </button>
        </div>

        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="bg-zip-surface rounded-xl border border-zip-border p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
              <div className="sm:col-span-2">
                {/* Filter narrows the dropdown below — item name, part no., description or project. */}
                <label className="block text-xs text-zip-muted mb-1">Filter Item / Part</label>
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zip-muted" />
                  <input
                    type="text"
                    className="zip-input pl-8 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    value={filters[i] ?? ''}
                    onChange={(e) => setFilters((f) => ({ ...f, [i]: e.target.value }))}
                    disabled={!clientId}
                    placeholder={clientId ? 'Type to narrow the list…' : 'Select a customer first'}
                    autoComplete="off"
                  />
                </div>

                {/* Select the item / part to dispatch. */}
                <label className="block text-xs text-zip-muted mt-2 mb-1">
                  Item Name / Part Description *
                </label>
                <select
                  className="zip-input text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  value={item.rackStorageId || ''}
                  onChange={(e) => selectByStorageId(i, e.target.value)}
                  disabled={!clientId || loadingStock}
                >
                  <option value="">
                    {!clientId
                      ? 'Select a customer first'
                      : loadingStock
                        ? 'Loading stock…'
                        : optionsFor(i).length === 0
                          ? (filters[i] ? 'No item matches this filter' : 'No stock available for this customer')
                          : 'Select item / part…'}
                  </option>
                  {optionsFor(i).map((o) => (
                    <option key={o._id} value={o._id}>{optionLabel(o)}</option>
                  ))}
                </select>

                {clientId && !loadingStock && (
                  <span className="text-[11px] text-zip-muted/70 mt-1 block">
                    {optionsFor(i).length} of {stockOptions.length} in-stock line(s)
                    {truncated && ' · list capped at 500 — use the filter to narrow it'}
                  </span>
                )}

                {item.availableQty !== null && (
                  <span className="text-xs text-emerald-600 font-mono mt-1 block">
                    Selected: {item.availableQty} {item.unit || 'pcs'} available in {item.rack?.code ?? '—'} · {item.floor ?? '—'}
                  </span>
                )}
              </div>
              <div>
                <label className="block text-xs text-zip-muted mb-1">Qty *</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max={item.availableQty || undefined}
                    className="zip-input text-sm"
                    value={item.quantity}
                    onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                  />
                  <span className="text-xs text-zip-muted font-mono whitespace-nowrap">{item.unit || 'pcs'}</span>
                </div>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="block text-xs text-zip-muted mb-1">Rack</label>
                  <input
                    type="text"
                    className="zip-input text-sm opacity-60 cursor-not-allowed"
                    value={item.rack ? `${item.rack?.code ?? '—'} · ${item.floor ?? '—'}` : ''}
                    readOnly
                  />
                </div>
                {items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                    className="text-zip-muted hover:text-zip-red transition-colors mb-0.5"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>

              {/* Invoice / part / batch detail */}
              <div className="col-span-2 sm:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 border-t border-zip-border">
                <div>
                  <label className="block text-xs text-zip-muted mb-1">Invoice No.</label>
                  <input type="text" className="zip-input text-sm" placeholder="INV-001"
                    value={item.invoiceNo} onChange={(e) => updateItem(i, 'invoiceNo', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-zip-muted mb-1">Invoice Date</label>
                  <input type="date" className="zip-input text-sm"
                    value={item.invoiceDate} onChange={(e) => updateItem(i, 'invoiceDate', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-zip-muted mb-1">Part Number</label>
                  <input type="text" className="zip-input text-sm" placeholder="PN-001"
                    value={item.partNumber} onChange={(e) => updateItem(i, 'partNumber', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-zip-muted mb-1">Part Rev.</label>
                  <input type="text" className="zip-input text-sm" placeholder="A"
                    value={item.partRev} onChange={(e) => updateItem(i, 'partRev', e.target.value)} />
                </div>
                {/* Part Description is merged into "Item Name / Part Description" above. */}
                <div>
                  <label className="block text-xs text-zip-muted mb-1">Box/Coil Qty</label>
                  <input type="number" min="0" className="zip-input text-sm" placeholder="0"
                    value={item.boxQty} onChange={(e) => updateItem(i, 'boxQty', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs text-zip-muted mb-1">Invoice Qty</label>
                  <input type="number" min="0" className="zip-input text-sm" placeholder="0"
                    value={item.invoiceQty} onChange={(e) => updateItem(i, 'invoiceQty', e.target.value)} />
                </div>
                <div className="col-span-2 sm:col-span-4">
                  <label className="block text-xs text-zip-muted mb-1">Batch No.</label>
                  <input type="text" className="zip-input text-sm" placeholder="BATCH-001"
                    value={item.batchNo} onChange={(e) => updateItem(i, 'batchNo', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="btn-primary flex items-center gap-2"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating…</>
          ) : 'Create Dispatch'}
        </button>
        <button type="button" onClick={() => navigate('/dispatch')} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
};

export default DispatchForm;
